import api from './api';

// Types for Admin Statistics
export interface PlatformOverviewStats {
  totalTenants: number;
  activeTenants: number;
  inactiveTenants: number;
  totalUsers: number;
  totalRevenue: number;
  totalQLeads: number;
  activeSubscriptions: number;
  tenantGrowthPercentage: number;
  userGrowthPercentage: number;
  revenueGrowthPercentage: number;
}

export interface TenantGrowthData {
  month: string;
  year: number;
  newTenants: number;
  totalTenants: number;
  previousPeriodNewTenants?: number;
}

export interface RevenueData {
  month: string;
  year: number;
  qLeadRevenue: number;
  subscriptionRevenue: number;
  totalRevenue: number;
}

export interface TenantRevenueBreakdown {
  tenantId: string;
  tenantName: string;
  revenue: number;
  qLeadCount: number;
  percentage: number;
}

export interface TopTenant {
  id: string;
  name: string;
  slug: string;
  qLeadCount: number;
  revenue: number;
  activeBrokers: number;
  trend: number[];
  status: 'active' | 'inactive' | 'suspended';
  plan: string;
}

export interface PendingActionsCount {
  pendingTopups: number;
  pendingVerifications: number;
  flaggedActivities: number;
  expiringSoonSubscriptions: number;
}

export interface SystemHealthStatus {
  apiResponseTime: number;
  databaseStatus: 'healthy' | 'degraded' | 'down';
  activeSessions: number;
  errorRate: number;
  lastChecked: string;
  uptime: number;
  memoryUsage: number;
  cpuUsage: number;
}

export interface AuditLogEntry {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'LOGIN' | 'LOGOUT' | 'APPROVE' | 'REJECT' | 'ACTIVATE' | 'DEACTIVATE';
  entityType: string;
  entityId: string;
  description: string;
  ipAddress?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface UserDistributionByRole {
  role: string;
  count: number;
  percentage: number;
}

export interface UserDistributionByTenant {
  tenantId: string;
  tenantName: string;
  userCount: number;
  percentage: number;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

// API Endpoints
const ADMIN_STATS_ENDPOINTS = {
  PLATFORM_OVERVIEW: '/admin/stats/overview',
  TENANT_GROWTH: '/admin/stats/tenant-growth',
  REVENUE_ANALYTICS: '/admin/stats/revenue',
  REVENUE_BY_TENANT: '/admin/stats/revenue/by-tenant',
  TOP_TENANTS: '/admin/stats/top-tenants',
  PENDING_ACTIONS: '/admin/stats/pending-actions',
  SYSTEM_HEALTH: '/admin/stats/system-health',
  AUDIT_LOGS_PREVIEW: '/admin/audit-logs/preview',
  USER_DISTRIBUTION_BY_ROLE: '/admin/stats/users/by-role',
  USER_DISTRIBUTION_BY_TENANT: '/admin/stats/users/by-tenant',
  EXPORT_DASHBOARD: '/admin/stats/export',
};

// Mock data generators for development
const generateMockPlatformStats = (): PlatformOverviewStats => ({
  totalTenants: 156,
  activeTenants: 142,
  inactiveTenants: 14,
  totalUsers: 2847,
  totalRevenue: 487500,
  totalQLeads: 15420,
  activeSubscriptions: 138,
  tenantGrowthPercentage: 12.5,
  userGrowthPercentage: 8.3,
  revenueGrowthPercentage: 15.7,
});

const generateMockTenantGrowth = (): TenantGrowthData[] => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentMonth = new Date().getMonth();
  const data: TenantGrowthData[] = [];
  let totalTenants = 85;

  for (let i = 0; i < 12; i++) {
    const monthIndex = (currentMonth - 11 + i + 12) % 12;
    const newTenants = Math.floor(Math.random() * 15) + 5;
    totalTenants += newTenants;
    data.push({
      month: months[monthIndex],
      year: monthIndex <= currentMonth ? 2026 : 2025,
      newTenants,
      totalTenants,
      previousPeriodNewTenants: Math.floor(Math.random() * 12) + 3,
    });
  }

  return data;
};

const generateMockRevenueData = (): RevenueData[] => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentMonth = new Date().getMonth();
  const data: RevenueData[] = [];

  for (let i = 0; i < 12; i++) {
    const monthIndex = (currentMonth - 11 + i + 12) % 12;
    const qLeadRevenue = Math.floor(Math.random() * 30000) + 15000;
    const subscriptionRevenue = Math.floor(Math.random() * 10000) + 5000;
    data.push({
      month: months[monthIndex],
      year: monthIndex <= currentMonth ? 2026 : 2025,
      qLeadRevenue,
      subscriptionRevenue,
      totalRevenue: qLeadRevenue + subscriptionRevenue,
    });
  }

  return data;
};

const generateMockTenantRevenueBreakdown = (): TenantRevenueBreakdown[] => {
  const tenants = [
    { id: '1', name: 'Prestige Realty', revenue: 45200, qLeadCount: 452 },
    { id: '2', name: 'Urban Homes', revenue: 38700, qLeadCount: 387 },
    { id: '3', name: 'Prime Properties', revenue: 32100, qLeadCount: 321 },
    { id: '4', name: 'City Estates', revenue: 28400, qLeadCount: 284 },
    { id: '5', name: 'Metro Realtors', revenue: 25800, qLeadCount: 258 },
    { id: '6', name: 'Horizon Homes', revenue: 22300, qLeadCount: 223 },
    { id: '7', name: 'Summit Realty', revenue: 18900, qLeadCount: 189 },
    { id: '8', name: 'Others', revenue: 95100, qLeadCount: 951 },
  ];

  const totalRevenue = tenants.reduce((sum, t) => sum + t.revenue, 0);

  return tenants.map(t => ({
    tenantId: t.id,
    tenantName: t.name,
    revenue: t.revenue,
    qLeadCount: t.qLeadCount,
    percentage: Math.round((t.revenue / totalRevenue) * 100),
  }));
};

const generateMockTopTenants = (): TopTenant[] => [
  {
    id: '1',
    name: 'Prestige Realty Group',
    slug: 'prestige-realty',
    qLeadCount: 452,
    revenue: 45200,
    activeBrokers: 28,
    trend: [320, 380, 410, 425, 440, 452],
    status: 'active',
    plan: 'Enterprise',
  },
  {
    id: '2',
    name: 'Urban Homes Network',
    slug: 'urban-homes',
    qLeadCount: 387,
    revenue: 38700,
    activeBrokers: 24,
    trend: [250, 290, 320, 350, 370, 387],
    status: 'active',
    plan: 'Professional',
  },
  {
    id: '3',
    name: 'Prime Properties LLC',
    slug: 'prime-properties',
    qLeadCount: 321,
    revenue: 32100,
    activeBrokers: 19,
    trend: [200, 230, 260, 285, 305, 321],
    status: 'active',
    plan: 'Professional',
  },
  {
    id: '4',
    name: 'City Estates Inc',
    slug: 'city-estates',
    qLeadCount: 284,
    revenue: 28400,
    activeBrokers: 17,
    trend: [180, 210, 240, 255, 270, 284],
    status: 'active',
    plan: 'Professional',
  },
  {
    id: '5',
    name: 'Metro Realtors',
    slug: 'metro-realtors',
    qLeadCount: 258,
    revenue: 25800,
    activeBrokers: 15,
    trend: [150, 175, 200, 225, 245, 258],
    status: 'active',
    plan: 'Starter',
  },
  {
    id: '6',
    name: 'Horizon Homes',
    slug: 'horizon-homes',
    qLeadCount: 223,
    revenue: 22300,
    activeBrokers: 14,
    trend: [140, 160, 180, 195, 210, 223],
    status: 'active',
    plan: 'Starter',
  },
  {
    id: '7',
    name: 'Summit Realty',
    slug: 'summit-realty',
    qLeadCount: 189,
    revenue: 18900,
    activeBrokers: 12,
    trend: [100, 120, 145, 165, 178, 189],
    status: 'active',
    plan: 'Starter',
  },
  {
    id: '8',
    name: 'Golden Key Estates',
    slug: 'golden-key',
    qLeadCount: 175,
    revenue: 17500,
    activeBrokers: 11,
    trend: [90, 110, 130, 150, 165, 175],
    status: 'active',
    plan: 'Starter',
  },
  {
    id: '9',
    name: 'Dream Properties',
    slug: 'dream-properties',
    qLeadCount: 162,
    revenue: 16200,
    activeBrokers: 10,
    trend: [85, 100, 120, 140, 155, 162],
    status: 'active',
    plan: 'Basic',
  },
  {
    id: '10',
    name: 'Elite Realty Services',
    slug: 'elite-realty',
    qLeadCount: 148,
    revenue: 14800,
    activeBrokers: 9,
    trend: [75, 90, 110, 125, 140, 148],
    status: 'active',
    plan: 'Basic',
  },
];

const generateMockPendingActions = (): PendingActionsCount => ({
  pendingTopups: 12,
  pendingVerifications: 8,
  flaggedActivities: 3,
  expiringSoonSubscriptions: 5,
});

const generateMockSystemHealth = (): SystemHealthStatus => ({
  apiResponseTime: 145,
  databaseStatus: 'healthy',
  activeSessions: 234,
  errorRate: 0.12,
  lastChecked: new Date().toISOString(),
  uptime: 99.97,
  memoryUsage: 67.3,
  cpuUsage: 42.8,
});

const generateMockAuditLogs = (): AuditLogEntry[] => [
  {
    id: '1',
    userId: 'u1',
    userName: 'John Admin',
    userEmail: 'john@admin.com',
    action: 'APPROVE',
    entityType: 'TopupRequest',
    entityId: 'tr1',
    description: 'Approved topup request of $500 for Prestige Realty',
    ipAddress: '192.168.1.1',
    createdAt: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
  },
  {
    id: '2',
    userId: 'u1',
    userName: 'John Admin',
    userEmail: 'john@admin.com',
    action: 'ACTIVATE',
    entityType: 'User',
    entityId: 'u25',
    description: 'Activated user account for Sarah Smith',
    ipAddress: '192.168.1.1',
    createdAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
  },
  {
    id: '3',
    userId: 'u2',
    userName: 'Sarah Manager',
    userEmail: 'sarah@admin.com',
    action: 'CREATE',
    entityType: 'Tenant',
    entityId: 't45',
    description: 'Created new tenant: Golden Key Estates',
    ipAddress: '192.168.1.50',
    createdAt: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
  },
  {
    id: '4',
    userId: 'u1',
    userName: 'John Admin',
    userEmail: 'john@admin.com',
    action: 'UPDATE',
    entityType: 'Tenant',
    entityId: 't12',
    description: 'Updated plan from Starter to Professional for Urban Homes',
    ipAddress: '192.168.1.1',
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '5',
    userId: 'u3',
    userName: 'Mike Support',
    userEmail: 'mike@admin.com',
    action: 'REJECT',
    entityType: 'TopupRequest',
    entityId: 'tr5',
    description: 'Rejected topup request - Invalid payment proof',
    ipAddress: '192.168.1.100',
    createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '6',
    userId: 'u2',
    userName: 'Sarah Manager',
    userEmail: 'sarah@admin.com',
    action: 'DEACTIVATE',
    entityType: 'User',
    entityId: 'u78',
    description: 'Deactivated user account for inactive broker',
    ipAddress: '192.168.1.50',
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '7',
    userId: 'u1',
    userName: 'John Admin',
    userEmail: 'john@admin.com',
    action: 'LOGIN',
    entityType: 'Session',
    entityId: 's123',
    description: 'Admin login from new device',
    ipAddress: '192.168.1.1',
    createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '8',
    userId: 'u4',
    userName: 'Lisa Admin',
    userEmail: 'lisa@admin.com',
    action: 'UPDATE',
    entityType: 'Settings',
    entityId: 'settings',
    description: 'Updated platform billing settings',
    ipAddress: '192.168.1.75',
    createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '9',
    userId: 'u1',
    userName: 'John Admin',
    userEmail: 'john@admin.com',
    action: 'APPROVE',
    entityType: 'TopupRequest',
    entityId: 'tr8',
    description: 'Approved topup request of $1000 for Metro Realtors',
    ipAddress: '192.168.1.1',
    createdAt: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '10',
    userId: 'u2',
    userName: 'Sarah Manager',
    userEmail: 'sarah@admin.com',
    action: 'CREATE',
    entityType: 'User',
    entityId: 'u95',
    description: 'Created new admin user: Support Team',
    ipAddress: '192.168.1.50',
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
];

const generateMockUserDistributionByRole = (): UserDistributionByRole[] => [
  { role: 'Owner', count: 156, percentage: 5.5 },
  { role: 'Manager', count: 312, percentage: 11.0 },
  { role: 'Broker', count: 2187, percentage: 76.8 },
  { role: 'Admin', count: 192, percentage: 6.7 },
];

const generateMockUserDistributionByTenant = (): UserDistributionByTenant[] => [
  { tenantId: '1', tenantName: 'Prestige Realty', userCount: 45, percentage: 12.8 },
  { tenantId: '2', tenantName: 'Urban Homes', userCount: 38, percentage: 10.8 },
  { tenantId: '3', tenantName: 'Prime Properties', userCount: 32, percentage: 9.1 },
  { tenantId: '4', tenantName: 'City Estates', userCount: 28, percentage: 8.0 },
  { tenantId: '5', tenantName: 'Metro Realtors', userCount: 24, percentage: 6.8 },
  { tenantId: '6', tenantName: 'Horizon Homes', userCount: 22, percentage: 6.3 },
  { tenantId: '7', tenantName: 'Summit Realty', userCount: 19, percentage: 5.4 },
  { tenantId: '8', tenantName: 'Golden Key', userCount: 17, percentage: 4.8 },
  { tenantId: '9', tenantName: 'Dream Properties', userCount: 15, percentage: 4.3 },
  { tenantId: '10', tenantName: 'Elite Realty', userCount: 14, percentage: 4.0 },
];

// Service with API calls (falls back to mock data)
export const adminStatsService = {
  async getPlatformOverview(): Promise<PlatformOverviewStats> {
    try {
      const response = await api.get<PlatformOverviewStats>(ADMIN_STATS_ENDPOINTS.PLATFORM_OVERVIEW);
      return response.data;
    } catch {
      // Return mock data for development
      return generateMockPlatformStats();
    }
  },

  async getTenantGrowth(dateRange?: DateRange): Promise<TenantGrowthData[]> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate);
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate);

      const response = await api.get<TenantGrowthData[]>(
        `${ADMIN_STATS_ENDPOINTS.TENANT_GROWTH}?${params.toString()}`
      );
      return response.data;
    } catch {
      return generateMockTenantGrowth();
    }
  },

  async getRevenueAnalytics(dateRange?: DateRange): Promise<RevenueData[]> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate);
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate);

      const response = await api.get<RevenueData[]>(
        `${ADMIN_STATS_ENDPOINTS.REVENUE_ANALYTICS}?${params.toString()}`
      );
      return response.data;
    } catch {
      return generateMockRevenueData();
    }
  },

  async getRevenueByTenant(): Promise<TenantRevenueBreakdown[]> {
    try {
      const response = await api.get<TenantRevenueBreakdown[]>(ADMIN_STATS_ENDPOINTS.REVENUE_BY_TENANT);
      return response.data;
    } catch {
      return generateMockTenantRevenueBreakdown();
    }
  },

  async getTopTenants(
    sortBy: 'qLeadCount' | 'revenue' | 'activeBrokers' = 'qLeadCount',
    limit: number = 10
  ): Promise<TopTenant[]> {
    try {
      const response = await api.get<TopTenant[]>(
        `${ADMIN_STATS_ENDPOINTS.TOP_TENANTS}?sortBy=${sortBy}&limit=${limit}`
      );
      return response.data;
    } catch {
      const mockData = generateMockTopTenants();
      // Sort based on sortBy parameter
      if (sortBy === 'revenue') {
        mockData.sort((a, b) => b.revenue - a.revenue);
      } else if (sortBy === 'activeBrokers') {
        mockData.sort((a, b) => b.activeBrokers - a.activeBrokers);
      }
      return mockData.slice(0, limit);
    }
  },

  async getPendingActions(): Promise<PendingActionsCount> {
    try {
      const response = await api.get<PendingActionsCount>(ADMIN_STATS_ENDPOINTS.PENDING_ACTIONS);
      return response.data;
    } catch {
      return generateMockPendingActions();
    }
  },

  async getSystemHealth(): Promise<SystemHealthStatus> {
    try {
      const response = await api.get<SystemHealthStatus>(ADMIN_STATS_ENDPOINTS.SYSTEM_HEALTH);
      return response.data;
    } catch {
      return generateMockSystemHealth();
    }
  },

  async getAuditLogsPreview(
    limit: number = 10,
    actionFilter?: string
  ): Promise<AuditLogEntry[]> {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      if (actionFilter) params.append('action', actionFilter);

      const response = await api.get<AuditLogEntry[]>(
        `${ADMIN_STATS_ENDPOINTS.AUDIT_LOGS_PREVIEW}?${params.toString()}`
      );
      return response.data;
    } catch {
      let mockData = generateMockAuditLogs();
      if (actionFilter) {
        mockData = mockData.filter(log => log.action === actionFilter);
      }
      return mockData.slice(0, limit);
    }
  },

  async getUserDistributionByRole(): Promise<UserDistributionByRole[]> {
    try {
      const response = await api.get<UserDistributionByRole[]>(
        ADMIN_STATS_ENDPOINTS.USER_DISTRIBUTION_BY_ROLE
      );
      return response.data;
    } catch {
      return generateMockUserDistributionByRole();
    }
  },

  async getUserDistributionByTenant(limit: number = 10): Promise<UserDistributionByTenant[]> {
    try {
      const response = await api.get<UserDistributionByTenant[]>(
        `${ADMIN_STATS_ENDPOINTS.USER_DISTRIBUTION_BY_TENANT}?limit=${limit}`
      );
      return response.data;
    } catch {
      return generateMockUserDistributionByTenant().slice(0, limit);
    }
  },

  async exportDashboardData(dateRange?: DateRange): Promise<Blob> {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.append('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.append('endDate', dateRange.endDate);

    const response = await api.get(
      `${ADMIN_STATS_ENDPOINTS.EXPORT_DASHBOARD}?${params.toString()}`,
      { responseType: 'blob' }
    );
    return response.data;
  },
};

export default adminStatsService;
